import { useHistory, useParams } from 'react-router-dom';
import { IMetricsApi, IResponse } from './TemplateMovementsCheck.types';
import { Button, Chip, CircularProgress, IconButton, Table, TableCell, TableContainer, TableRow, Box, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowEditStopReasons, GridEventListener } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState } from 'react';
import { cpfMask } from 'utils/cpf';
import { formatCNPJ } from 'utils/is-valid-cnpj';
import { ptBR } from '@mui/x-data-grid/locales';
import { formatActivityStatus, formatDate, formatMovementType, formatStatus, formatType, sortApps, sortRelatedActivities } from './TemplateMovementsCheck.utils';
import { Metrics, Modal } from 'v2/components';
import { Title, Label, TableLabel, TableText } from "./TemplateMovementsCheck.styled"
import { ArrowBack, Close, PestControl } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useAlert } from 'contexts/alert';
import ApprovedSVG from 'components/svg/approved';
import dayjs from 'dayjs';
import { useAuth } from 'contexts/auth';
import api from 'services/api';
import { formatCurrency } from 'v2/pages/SetupContract/Life/WorkspaceShared/Rules/Rules.utils';

export function TemplateMovementsCheckView({ data, contractType }: { data: IResponse, contractType: string }) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string };
  const { openAlert } = useAlert();
  const user = useAuth();
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState<"DEFAULT" | "APPROVE" | "REVIEW">("DEFAULT");
  const [isModalActivityOpen, setIsModalActivityOpen] = useState(false);
  const [isModalApproveOpen, setIsModalApproveOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvedMetrics, setApprovedMetrics] = useState<{ label: string, value: string }[]>([]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut && event) {
      event.defaultMuiPrevented = true;
    }
  };

  async function handleApprove() {
    try {
      setIsApproving(true);

      const beingApproved = [ ...data.people.filter(p =>
        p.status === "READY" &&
        p.movementType &&
        p.movementType !== '' &&
        p.movementType.toLowerCase() !== 'maintenance'
      ) ];
      const metrics = new Array();
      metrics.push({ label: "Total", value: String(beingApproved?.length ?? 0) });
      metrics.push({ label: "Inclusões", value: String(beingApproved?.filter(p => p?.movementType === "inclusion")?.length ?? 0) });
      metrics.push({ label: "Exclusões", value: String(beingApproved?.filter(p => p?.movementType === "exclusion")?.length ?? 0) });
      metrics.push({ label: "Alterações", value: String(beingApproved?.filter(p => p?.movementType === "change")?.length ?? 0) });
      metrics.push({ label: "Transferências", value: String(beingApproved?.filter(p => p?.movementType === "transfer")?.length ?? 0) });

      const approveWorkspaceActivityId = data.activities.find(a => a.slug === "app_approve")?.workspaceActivityId;
      const payload = beingApproved.map(p => ({ dagRunId: p.dagRunId, taxPayerId: p.taxPayerId }));

      if (!approveWorkspaceActivityId) throw new Error("Identificador do APP Aprovação não encontrado.");
      if (!payload.length) throw new Error("Não há beneficiários para serem aprovados.");

      const metricsApi: IMetricsApi = {
        admissions: String(beingApproved?.filter(p => p?.movementType === "inclusion")?.length ?? 0),
        exclusions: String(beingApproved?.filter(p => p?.movementType === "exclusion")?.length ?? 0),
        changes: String(beingApproved?.filter(p => p?.movementType === "change")?.length ?? 0),
        total: String(beingApproved?.length ?? 0),
        approvedDate: dayjs().format("DD/MM/YYYY"),
        approvedBy: user?.user?.name ?? undefined,
      };
      

      await api.post(`/v2/apps/approve/${approveWorkspaceActivityId}`, { payload, metrics: metricsApi });

      setApprovedMetrics([ ...metrics ]);
      setCurrentStep("REVIEW");
      openAlert('Massa movimentada aprovada com sucesso.', 'success');
    } catch (error) {
      if (error instanceof Error && !!error.message?.length) {
        return openAlert(error.message, 'error');
      }

      openAlert('Erro ao aprovar massa movimentada.', 'error');
    } finally {
      setIsApproving(false);
    }
  }

  const rows = data.people.map((person, index) => ({
    id: person.id || `${index + 1}_${Date.now()}`,
    name: person.name || '-',
    taxPayerId: person.taxPayerId ? cpfMask(person.taxPayerId) : '-',
    taxPayerIdEmployee: person.taxPayerIdEmployee ? cpfMask(person.taxPayerIdEmployee) : '-',
    status: person.status,
    movementType: formatMovementType(person.movementType),
    type: formatType(person.type),
    plan: person.plan ?? '-',
    employeeStatus: person.employeeStatus ?? '-',
    negotiation: person.negotiation ?? '-',
    cobertura: person.cobertura ? formatCurrency(Number(person.cobertura)) : '-',
    appliedValue: person.appliedValue ? formatCurrency(person.appliedValue) : "-",
    vigencyStartDate: person.vigencyStartDate ?? null,
    vigencyEndDate: person.vigencyEndDate ?? null,
    hiringDate: person.hiringDate ?? null,
    dismissalDate: person.dismissalDate ?? null,
    startPriorNoticeDate: person.priorNoticeStartDate ?? null,
    endPriorNoticeDate: person.priorNoticeEndDate ?? null,
    exclusionDateType: person.exclusionDateType ?? null,
    effectiveExclusionDate: person.effectiveExclusionDate ?? null,
    role: person.role ?? '-',
    city: person.city ?? '-',
    state: person.state ?? '-',
    hiringEventDate: person.hiringEventDate ?? '-',
    hiringSentDate: person.hiringSentDate ?? '-',
    dismissalEventDate: person.dismissalEventDate ?? '-',
    dismissalSentDate: person.dismissalSentDate ?? '-',
    competencyLabel: person.competencyLabel ?? '-',
    isTransfer: person.isTransfer ?? false,
    transferDirection: person.transferDirection ?? null,
    transferOriginCnpj: person.transferOriginCnpj ?? null,
    transferDestinationCnpj: person.transferDestinationCnpj ?? null,
    transferPlanChanged: person.transferPlanChanged ?? null,
  }));

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome Completo',
      width: 300,
      sortable: true,
    },
    {
      field: 'taxPayerId',
      headerName: 'CPF do Beneficiário',
      width: 160,
      sortable: true,
    },
    {
      field: 'taxPayerIdEmployee',
      headerName: 'CPF do Titular',
      width: 160,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      type: 'singleSelect',
      valueOptions: [
        { value: 'READY', label: 'Pronto' },
        { value: 'SKIPED', label: 'Retido' },
        { value: 'DELAYED', label: 'Em espera' },
        { value: 'RETAINED', label: 'Retificar' },
      ], // {value, label} pairs
      renderCell: (params: GridRenderCellParams) => {
        const { label, backgroundColor, color } = formatStatus(params.row.status);
        return (
          <Chip
            label={label}
            size="small"
            sx={{ backgroundColor: backgroundColor, color: color, fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'movementType',
      headerName: 'Tipo de movimentação',
      width: 230,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.isTransfer) return <span>{params.value}</span>;
        const origin = params.row.transferOriginCnpj ? formatCNPJ(params.row.transferOriginCnpj, false) : '?';
        const dest = params.row.transferDestinationCnpj ? formatCNPJ(params.row.transferDestinationCnpj, false) : '?';
        const planNote = params.row.transferPlanChanged ? 'com mudança de plano' : 'mesmo plano';
        const tooltip = `Originada de transferência ${origin} → ${dest} (${planNote})`;
        // Same-plan transfers already read "Transferência"; just make the label hoverable.
        // Plan-changed transfers read "Exclusão"/"Inclusão", so add the chip to flag the origin.
        if (params.value === 'Transferência') {
          return (
            <Tooltip arrow title={tooltip}>
              <span style={{ cursor: 'help' }}>{params.value}</span>
            </Tooltip>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <span>{params.value}</span>
            <Tooltip arrow title={tooltip}>
              <Chip
                label="Transferência"
                size="small"
                sx={{ backgroundColor: '#E8EAFF', color: '#3D43B3', fontWeight: 500, cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Tipo do Beneficiário',
      width: 150,
      sortable: true,
    },
    ...(contractType !== "LIFE" ? [{
      field: 'plan',
      headerName: 'Plano',
      width: 200,
      sortable: true,
    }] : []),
    ...(contractType === "LIFE" ? [{
      field: 'employeeStatus',
      headerName: 'Situação',
      width: 200,
      sortable: true,
    }] : []),
    ...(contractType === "LIFE" ? [{
      field: 'appliedValue',
      headerName: 'Prêmio',
      width: 200,
      sortable: true,
    }] : []),
    ...(contractType === "LIFE" ? [{
      field: 'cobertura',
      headerName: 'Cobertura',
      width: 200,
      sortable: true,
    }] : []),
    ...(contractType === "LIFE" ? [{
      field: 'negotiation',
      headerName: 'Negociação',
      width: 200,
      sortable: true,
    }] : []),
    ...(contractType === "LIFE" ? [{
      field: 'competencyLabel',
      headerName: 'Competência',
      width: 150,
      sortable: true,
    }] : []),
    ...(contractType !== "LIFE" ? [{
      field: 'vigencyStartDate',
      headerName: 'Data inicio plano',
      width: 180,
      sortable: true,
      type: 'date' as const,
      valueGetter: (value: string | null) => value ? new Date(value) : null,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    }] : []),
    ...(contractType !== "LIFE" ? [{
      field: 'vigencyEndDate',
      headerName: 'Data fim plano',
      width: 180,
      sortable: true,
      type: 'date' as const,
      valueGetter: (value: string | null) => value ? new Date(value) : null,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    }] : []),
    {
      field: 'hiringDate',
      headerName: 'Data de admissão',
      width: 180,
      sortable: true,
      type: 'date' as const,
      valueGetter: (value: string | null) => value ? new Date(value) : null,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    },
    {
      field: 'hiringSentDate',
      headerName: 'Envio ao eSocial (Admissão)',
      width: 200,
      sortable: true,
    },
    {
      field: 'dismissalDate',
      headerName: 'Data de demissão',
      width: 200,
      sortable: true,
      type: 'date' as const,
      valueGetter: (value: string | null) => value ? new Date(value) : null,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return '-';
        const isUsed = params.row.exclusionDateType === 'DISMISSAL_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {formatDate(params.value)}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'dismissalSentDate',
      headerName: 'Envio ao eSocial (Desligamento)',
      width: 220,
      sortable: true,
    },
    {
      field: 'endPriorNoticeDate',
      headerName: 'Término Aviso Prévio',
      width: 220,
      sortable: true,
      type: 'date' as const,
      valueGetter: (value: string | null) => {
        if (!value) return null;
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.toDate() : null;
      },
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        const isUsed = params.row.exclusionDateType === 'PRIOR_NOTICE_END_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {formatDate(params.value)}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'role',
      headerName: 'Cargo',
      width: 180,
      sortable: true,
    },
    {
      field: 'city',
      headerName: 'Cidade',
      width: 180,
      sortable: true,
    },
    {
      field: 'state',
      headerName: 'Estado',
      width: 180,
      sortable: true,
    },
  ];

  return (
    <>
      {currentStep === "DEFAULT" && (
        <>
          <Modal open={isModalActivityOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Atividades relacionadas</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalActivityOpen(false)}
              />
            </div>
            <TableText>
              Para ter Conferir Movimentações atualizada verifique se as
              atividades abaixo estão atualizadas de acordo com sua execução
              programada.
            </TableText>
            <div style={{ width: "100%", maxHeight: "400px", overflow: "scroll" }}>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: "70px" }}>
                    <TableCell>
                      <TableLabel>Nome</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Última atualização</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Execução programada</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Status</TableLabel>
                    </TableCell>
                  </TableRow>
                  {sortRelatedActivities(data.activities).map(activity => (
                    <TableRow key={activity.slug} style={{ height: "70px" }}>
                      <TableCell>{activity.name}</TableCell>
                      <TableCell>{formatDate(String(activity.lastExecution), "timestamp_2")}</TableCell>
                      <TableCell>{formatDate(String(activity.scheduledExecution), "timestamp_2")}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatActivityStatus(activity.status).label}
                          size="small"
                          sx={{ backgroundColor: formatActivityStatus(activity.status).backgroundColor, color: formatActivityStatus(activity.status).color, fontWeight: "500", opacity: "0.8" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </div>
          </Modal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title>Conferir movimentações</Title>
            <Button
              variant="outlined"
              style={{ padding: "1px 16px 1px 16px", fontSize: "14px", fontWeight: "600" }}
              onClick={() => setIsModalActivityOpen(true)}
            >
              Atividades relacionadas
            </Button>
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Dados atualizados em</Label>
              <Label>
                <strong>{formatDate(String(data.lastExecution), "timestamp")}</strong>
              </Label>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Status</Label>
              <Chip
                label={formatActivityStatus(data.status).label}
                size="small"
                sx={{ backgroundColor: formatActivityStatus(data.status).backgroundColor, color: formatActivityStatus(data.status).color, fontWeight: "500", opacity: "0.8" }}
              />
            </div>
          </div>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
            <Metrics title="Movimentações" metrics={data.metricsMovement.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
            <Metrics title="Detalhamento por status" metrics={data.metricsStatus.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
          </div>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
            onRowEditStop={handleRowEditStop}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            {sortApps(data.activities.filter(activity => activity.type === "APP" && activity.slug !== "app_approve")).map(activity => (
              <>
                {activity.slug === "dag_excecao" ? (
                  <Button
                    variant='outlined'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/excecao/${activity.workspaceActivityId}`)}
                  >
                    {activity.name}
                  </Button>
                ) : activity.slug === "app_transfer" ? (
                  <Button
                    variant='outlined'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    disabled
                  >
                    {activity.name}
                  </Button>
                ) : (
                  <Button
                    key={activity.workspaceActivityId}
                    variant='outlined'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/${activity.slug}/${activity.workspaceActivityId}`)}
                  >
                    {activity.name}
                  </Button>
                )}
              </>
            ))}
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setCurrentStep("APPROVE")}
            >
              Revisar movimentações
            </Button>
          </div>
        </>
      )}
      {currentStep === "APPROVE" && (
        <>
          <Modal open={isModalApproveOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Aprovar movimentações e enviar para corretora?</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalApproveOpen(false)}
              />
            </div>
            <TableText>
              Você está prestes a aprovar as movimentações. Seu corretor(a) será notificado da aprovação
            </TableText>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
              <Button
                variant="outlined"
                color="error"
                style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                disabled={isApproving}
                onClick={() => setIsModalApproveOpen(false)}
              >
                Voltar para revisão
              </Button>
              <Button
                variant='contained'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isApproving}
                onClick={handleApprove}
              >
                {!isApproving ? "Aprovar" : <CircularProgress size="20px"/>}
              </Button>
            </div>
          </Modal>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => setCurrentStep("DEFAULT")}
            >
              <ArrowBack color="primary" />
            </IconButton>
          <Title>Revisar movimentações</Title>
          </div>
          <div style={{"display":"flex","gap":"1rem"}}>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Total</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "READY" && p?.movementType && p?.movementType !== '' && p?.movementType?.toLowerCase() !== 'maintenance')?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Inclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "READY")?.filter(p => p?.movementType === "inclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Exclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "READY")?.filter(p => p?.movementType === "exclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Alterações</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "READY")?.filter(p => p?.movementType === "change")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Transferências</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "READY")?.filter(p => p?.movementType === "transfer")?.length ?? 0}</span>
            </div>
          </div>
          <DataGrid
            rows={rows.filter(r =>
              r.status === "READY" &&
              r.movementType &&
              r.movementType !== '-' &&
              r.movementType.toLowerCase() !== 'maintenance'
            )}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
            onRowEditStop={handleRowEditStop}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            <Button
              variant="outlined"
              style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600, width: "130px" }}
              startIcon={<ChevronLeftIcon color="primary" style={{ height: "22px", marginLeft: "-8px"}} />}
              onClick={() => setCurrentStep("DEFAULT")}
            >
              Voltar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              disabled={!data.people.filter(p =>
                p?.status === "READY" &&
                p?.movementType &&
                p?.movementType !== '-' &&
                p?.movementType?.toLowerCase() !== 'maintenance'
              )?.length}
              onClick={() => setIsModalApproveOpen(true)}
            >
              Aprovar movimentações
            </Button>
          </div>
        </>
      )}
      {currentStep === "REVIEW" && (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "30px", marginTop: "30px", marginBottom: "30px" }}>
          <ApprovedSVG />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Aprovado com sucesso</h1>
            <p style={{ fontSize: '16px', color: '#6F6F78', margin: "0", padding: "0" }}>Agora as movimentação serão processadas pelo corretor(a).</p>
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0px 50px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Aprovação realizada em</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{dayjs().format("DD/MM/YYYY")}</span>
            </div>
            {!!user?.user?.name && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0px 50px" }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Aprovado por</p>
                <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{user.user.name}</span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            {approvedMetrics.map(({ label, value }) => (
              <div style={{ width: '138px', height: "84px", border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>{label}</p>
                <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "400px", gap: "16px", marginTop: "25px" }}>
            <Button
              variant='contained'
              style={{"borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","fontWeight":600,width: "100%"}}
              onClick={() => window.location.reload()}
            >
              Retornar para atividade
            </Button>
            <Button
              variant='outlined'
              style={{"borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","fontWeight":600,width: "100%"}}
              disabled
            >
              Ver histórico de aprovações
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
